import React from 'react';
import axios, { CancelToken, Method } from 'axios';
import i18n from './i18n';

import axiosInstance from './axiosInstance';

import ShuttleIcon from './assets/icons/ShuttleIcon';
import CommercialIcon from './assets/icons/CommercialIcon';
import OverstockIcon from './assets/icons/OverstockIcon';
import RecallIcon from './assets/icons/RecallIcon';
import DollarIcon from './assets/icons/DollarIcon';
import BrazilCurrencyIcon from './assets/icons/BrazilCurrencyIcon';

import {
  ORDER_TYPE_SAT_TO_SAT,
  ORDER_TYPE_COMMERCIAL,
  ORDER_TYPE_OVERSTOCK,
  ORDER_TYPE_RECALL,
  POG_DYNAMIC_SLOT,
  POG_OVERSTOCK,
  DESKTOP,
  CT40,
  TC75,
  USER_DATA,
  BYTE,
  ANDROID,
  ORDER_TYPE_COMMERCIAL_EP,
  PADDING_LENGTH,
  COUNTRY_US,
  COUNTRY_MEXICO,
  COUNTRY_BRAZIL,
} from './constants';

import { Falsy, OrderType, Pick, PickTour, DynamicSlotDetails, UserData, LogType, PickLocation } from './types';
import { OrdersByType } from './redux/reducers/types';

const translationRoot = 'UTILS';

export const mergeClassNames = (...classNames: (string | Falsy)[]) =>
  classNames.filter(className => className).join(' ');

export const stripLeadingZeroes: (value: string) => string = value => value && value.replace(/^0+/, '');

const padLeadingZeroes = (length: number) => (value: string) => {
  if (value.length < length) {
    return `${'0'.repeat(length - value.length)}${value}`;
  }
  return value;
};

export const isCommercialOrder = (orderType?: OrderType) =>
  orderType && (orderType === ORDER_TYPE_COMMERCIAL || orderType === ORDER_TYPE_COMMERCIAL_EP);

export const isRecallOrder = (orderType?: OrderType) =>
  orderType && orderType === ORDER_TYPE_RECALL;

export const padTo8LeadingZeroes = padLeadingZeroes(8);
// Matches string that starts with EP- followed by 1 or more numbers.
export const epRegEx = /^EP-\d+/;

export const displayOrderType = (
  orderType: OrderType,
  deliveryMethod?: string | null,
  orderDestination?: string | null
) => {
  switch (orderType) {
    case ORDER_TYPE_SAT_TO_SAT: {
      return i18n.t(`${translationRoot}.SAT_TO_SAT`);
    }
    /*
      TODO: clean this hack up - once the new order type has been in prod for a while, we can separate the cases out here
      Then we will have no need for the extra orderDestination parameter in the method.
    */
    case ORDER_TYPE_COMMERCIAL:
    case ORDER_TYPE_COMMERCIAL_EP: {
      if (deliveryMethod || orderDestination) {
        if (orderDestination && orderDestination.match(epRegEx) && !deliveryMethod) {
          return `${i18n.t(`${translationRoot}.EXPANDED_PART`)}`;
        }
        if (!deliveryMethod && orderDestination && !orderDestination.match(epRegEx)) {
          return i18n.t(`${translationRoot}.COMMERCIAL`);
        }
        return `${
          orderDestination && orderDestination.match(epRegEx) ? `EP` : `${i18n.t(`${translationRoot}.COMMERCIAL`)}`
        } - ${i18n.t(`${translationRoot}.${deliveryMethod === 'Commercial - Delivery' ? 'DELIVERY' : 'WALK_IN'}`)}`;
      }
      return i18n.t(`${translationRoot}.COMMERCIAL`);
    }
    case ORDER_TYPE_OVERSTOCK: {
      return i18n.t(`${translationRoot}.OVERSTOCK`);
    }
    case ORDER_TYPE_RECALL: {
      return i18n.t(`${translationRoot}.RECALL`);
    }
    default: {
      return '';
    }
  }
};

export const determineIconByOrderType = ({ orderType, small }: { orderType: OrderType; small?: boolean }) => {
  switch (orderType) {
    case ORDER_TYPE_SAT_TO_SAT: {
      return <ShuttleIcon small={small} />;
    }
    case ORDER_TYPE_COMMERCIAL:
    case ORDER_TYPE_COMMERCIAL_EP: {
      return <CommercialIcon small={small} />;
    }
    case ORDER_TYPE_OVERSTOCK: {
      return <OverstockIcon small={small} />;
    }
    case ORDER_TYPE_RECALL: {
      return <RecallIcon small={small} />;
    }
    default: {
      return '';
    }
  }
};

export const determineCurrencyIcon = (currencyCode?: string) => {
  const normalized = currencyCode?.trim().toUpperCase();
  switch (normalized) {
    case 'BRL': {
      return <BrazilCurrencyIcon />;
    }
    case 'USD':
    case 'MXN':
    default: {
      return <DollarIcon />;
    }
  }
};

export const formatCurrencyAmount = (amount: number, currencyCode?: string): string => {
  const normalized = currencyCode?.trim().toUpperCase();
  switch (normalized) {
    case 'BRL': {
      // Brazil: 12.345,67 (period for thousands, comma for decimal)
      return amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    case 'MXN': {
      // Mexico: 12,345.67 (comma for thousands, period for decimal)
      return amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    case 'USD':
    default: {
      // USA: 12,345.67 (comma for thousands, period for decimal)
      return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};

export const callAPI = (options: {
  method: Method;
  url: string;
  headers?: { [key: string]: string } | null;
  languageHeader?: string;
  data?: any;
  withCredentials?: boolean;
  cancelToken?: CancelToken;
}) => {
  const { method, url, headers, data, withCredentials, cancelToken } = options;

  const logInValuesJSON = sessionStorage.getItem('loginData');
  const logInValues = logInValuesJSON && JSON.parse(logInValuesJSON);
  return axiosInstance.request({
    url,
    method,
    data,
    withCredentials,
    headers: {
      ...headers,
      Authorization: process.env.NODE_ENV === 'test' ? 'Bearer 1234' : `Bearer ${logInValues.access_token}`,
      'Content-Type': 'application/json'
    },
    cancelToken,
  });
};

export const generatePickID = (pick: Pick) => `${pick.orderType}:${pick.orderID}:${pick.lineNumber}`;

export const displayDestination = ({
  orderType,
  destination,
}: {
  orderType: OrderType;
  destination: string | null;
}) => {
  if (destination) {
    return [ORDER_TYPE_SAT_TO_SAT, ORDER_TYPE_OVERSTOCK].includes(orderType)
      ? `${i18n.t(`${translationRoot}.STORE`)} ${stripLeadingZeroes(destination)}`
      : destination;
  }
  return `${i18n.t(`${translationRoot}.NO_DESTINATION`)}`;
};

export const padTo2LeadingZeroes = padLeadingZeroes(2);

export const padTo3LeadingZeroes = padLeadingZeroes(3);

export const padTo6LeadingZeroes = padLeadingZeroes(6);

const VALID_COUNTRY_CODES = [COUNTRY_US, COUNTRY_MEXICO, COUNTRY_BRAZIL] as const;

// Map country codes to IANA timezone identifiers
const COUNTRY_TIMEZONES: Record<string, string> = {
  [COUNTRY_US]: 'America/New_York',        // Eastern Time (covers most US operations)
  [COUNTRY_MEXICO]: 'America/Mexico_City', // Central Mexico Time
  [COUNTRY_BRAZIL]: 'America/Sao_Paulo',   // Brasília Time (covers most Brazil operations)
};

export const displayDate = ({ date, countryCode }: { date: string; countryCode?: string }) => {
  const validatedCountryCode =
    countryCode && (VALID_COUNTRY_CODES as readonly string[]).includes(countryCode) ? countryCode : undefined;

  const dateobj = new Date(date);
  const timeZone = validatedCountryCode ? COUNTRY_TIMEZONES[validatedCountryCode] : COUNTRY_TIMEZONES[COUNTRY_US];

  // Use Intl.DateTimeFormat to properly convert to country timezone
  const useDDMMFormat = validatedCountryCode === COUNTRY_MEXICO || validatedCountryCode === COUNTRY_BRAZIL;
  const use24Hour = validatedCountryCode === COUNTRY_BRAZIL;

  // Get date parts in the store's timezone
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: !use24Hour,
  });

  const dateParts = dateFormatter.formatToParts(dateobj);
  const month = dateParts.find(p => p.type === 'month')?.value || '01';
  const day = dateParts.find(p => p.type === 'day')?.value || '01';
  const year = dateParts.find(p => p.type === 'year')?.value || '1970';

  const dateString = useDDMMFormat ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;

  // Format time in the store's timezone
  let timeString = timeFormatter.format(dateobj);

  // Normalize AM/PM to lowercase and standardize spacing (matching existing UI)
  if (!use24Hour) {
    // Ensure AM/PM is lowercase and has single space before it
    timeString = timeString.toLowerCase().replace(/\s+/g, ' ');
  } else {
    // Fix edge case: Intl.DateTimeFormat sometimes formats midnight as "24:00" instead of "00:00"
    timeString = timeString.replace(/^24:/, '00:');
  }

  return `${dateString} - ${timeString}`;
};

export const isToday = (dateInQuestion: string) => {
  const today = new Date();
  const formattedDate = new Date(dateInQuestion);
  return (
    formattedDate.getUTCDate() === today.getUTCDate() &&
    formattedDate.getUTCMonth() === today.getUTCMonth() &&
    formattedDate.getUTCFullYear() === today.getUTCFullYear()
  );
};

export const calculateAmountToPick = (pick: Pick) => {
  if (isRecallOrder(pick.orderType)) {
    const originalTotal = pick.totalQuantity;
    const keepQuantity = pick.keepQuantityOnHand || pick.keepQuantityOnHand === 0 ? pick.keepQuantityOnHand : null;
    const updatedQuantity =
      pick.locations.length > 0 ? pick.locations[pick.locations.length - 1].updatedQuantity : null;
    const quantityOnHand = updatedQuantity || updatedQuantity === 0 ? updatedQuantity : pick.quantityOnHand;

    const amountToPick = (quantityOnHand || quantityOnHand === 0) && (keepQuantity || keepQuantity === 0)
      ? Math.max(quantityOnHand - keepQuantity, 0)
      : originalTotal;

    return amountToPick;
  }
  return pick.totalQuantity;
};

export const calculateQuantitiesForExitReview = (pickTour: PickTour) => {
  return pickTour.picks.reduce(
    (quantity, pick) => quantity + (pick.pickedQuantity < calculateAmountToPick(pick) ? 0 : pick.pickedQuantity),
    0
  );
};

export const calculatePickedQuantity = (pickTour: PickTour) => {
  return pickTour.picks.reduce((quantity, pick) => quantity + pick.pickedQuantity, 0);
};

export const calculateResolvedPickPickedQuantity = (pickTour: PickTour) =>
  pickTour.picks.reduce(
    (quantity, pick) =>
      quantity + (pick.pickedQuantity === calculateAmountToPick(pick) || pick.isSkipped ? pick.pickedQuantity : 0),
    0
  );

export const calculateUnresolvedPickPickedQuantity = (pickTour: PickTour) =>
  pickTour.picks.reduce(
    (quantity, pick) =>
      quantity + (pick.pickedQuantity < calculateAmountToPick(pick) && !pick.isSkipped ? pick.pickedQuantity : 0),
    0
  );

export const calculateTotalQuantity = (pickTour: PickTour) =>
  pickTour.picks.reduce((quantity, pick) => quantity + calculateAmountToPick(pick), 0);

export const updatedQuantityTotal = (activePick: Pick | null) => {
  if (!activePick) {
    return null;
  }

  const locationsUpdated = activePick?.locations.filter(
    (location: PickLocation) => location.updatedQuantity && location
  );

  if (locationsUpdated?.length) {
    const updatedQuantitySum = locationsUpdated.reduce(
      (previousValue, currentValue: PickLocation) =>
        currentValue.updatedQuantity ? previousValue + currentValue.updatedQuantity : previousValue,
      0
    );

    return updatedQuantitySum;
  }

  return null;
};

export const calculateSkippedQuantity = (pickTour: PickTour) =>
  pickTour.picks.reduce(
    (quantity, pick) => (pick.isSkipped ? quantity + (calculateAmountToPick(pick) - pick.pickedQuantity) : quantity),
    0
  );

export const sortisEligibleForPicking = (pickTour: PickTour) =>
  pickTour.picks.sort((pickA, pickB) => Number(pickB.isEligibleForPicking) - Number(pickA.isEligibleForPicking));

export const setInputIfDigits = (params: { inputSetter: (value: string) => void; value: string }) => {
  const { inputSetter, value } = params;
  if (/^\d*$/.test(value)) {
    inputSetter(value);
  }
};

// needs access to configs
export const determineDeviceType = () => {
  const deviceInfo = navigator.userAgent;
  if (deviceInfo.includes(TC75)) {
    return TC75;
  }
  if (deviceInfo.includes(ANDROID)) {
    return CT40;
  }
  return DESKTOP;
};

/* The sequence field for dynamically slotted items needs to be parsed using the following instructions:
     - The field comes back as an 8 or 9 digit number.
     - The first digit (or first two digits if a 9 digit number) represents the Zone.
     - The next two digits represent the Aisle.
     - The last five digits represent the Slot.
  */

export const parseDynamicSlotDetails = (sequence: string): DynamicSlotDetails => ({
  zone: stripLeadingZeroes(sequence.slice(0, -7)),
  aisle: stripLeadingZeroes(sequence.slice(-7, -5)),
  slot: stripLeadingZeroes(sequence.slice(-5)),
});

export const sortPicksByLocation = ({
  picks,
  shouldUseFirstLocation,
}: {
  picks: Pick[];
  shouldUseFirstLocation?: boolean;
}) =>
  [...picks].sort((pickA, pickB) => {
    // SDSACC-721 -Below sorting is not required for the recall flow.
    if (isRecallOrder(pickA.orderType) || isRecallOrder(pickB.orderType)) {
      return 0;
    }
    const locationA = pickA.locations[shouldUseFirstLocation ? 0 : pickA.locationIndex];
    const locationB = pickB.locations[shouldUseFirstLocation ? 0 : pickB.locationIndex];
    if (locationA) {
      if (locationB) {
        if (locationA.planogramName === POG_DYNAMIC_SLOT) {
          if (locationB.planogramName === POG_DYNAMIC_SLOT) {
            return locationA.itemSequence - locationB.itemSequence;
          }
          return -1;
        }
        if (locationB.planogramName === POG_DYNAMIC_SLOT) {
          return 1;
        }
        if (locationA.planogramName === POG_OVERSTOCK) {
          if (locationB.planogramName === POG_OVERSTOCK) {
            return locationA.itemSequence - locationB.itemSequence;
          }
          return -1;
        }
        if (locationB.planogramName === POG_OVERSTOCK) {
          return 1;
        }
        if (locationA.vdlSequence !== null) {
          if (locationB.vdlSequence !== null) {
            if (locationA.vdlSequence === locationB.vdlSequence) {
              return locationA.itemSequence - locationB.itemSequence;
            }
            return locationA.vdlSequence - locationB.vdlSequence;
          }
          return -1;
        }
        if (locationB.vdlSequence !== null) {
          return 1;
        }
        return 0;
      }
      return -1;
    }
    if (locationB) {
      return 1;
    }
    return 0;
  });

/**
 * Finds the next closest pick based on proximity (itemSequence) for commercial orders.
 * Used for proximity-based routing when "Next Location" is clicked.
 *
 * Priority order:
 * 1. DYNAMIC_SLOT items (sorted by itemSequence)
 * 2. OVERSTOCK items (sorted by itemSequence)
 * 3. POG_FOG items (sorted by vdlSequence, then itemSequence)
 * 4. Other items
 *
 * Tie-breaker: When multiple items have the same itemSequence, prefer the current item.
 *
 * @param picks - Array of picks to search
 * @param currentPickID - Optional ID of the current pick (for tie-breaking or exclusion)
 * @param excludeCurrentPick - If true, exclude the current pick from results (default: false)
 * @returns The next closest pick, or null if none found
 */
export const findNextClosestPick = ({
  picks,
  currentPickID,
  excludeCurrentPick = false,
}: {
  picks: Pick[];
  currentPickID?: string;
  excludeCurrentPick?: boolean;
}): Pick | null => {
  // Filter to unpicked, non-skipped items
  const availablePicks = picks.filter(
    pick =>
      pick.pickedQuantity < calculateAmountToPick(pick) &&
      !pick.isSkipped &&
        pick.isEligibleForPicking &&
        pick.locations.length > pick.locationIndex &&
      (!excludeCurrentPick || generatePickID(pick) !== currentPickID)
  );

  if (availablePicks.length === 0) {
    return null;
  }

  // Separate picks by location type for priority sorting
  const dynamicSlotPicks = availablePicks.filter(
    pick => pick.locations[pick.locationIndex]?.planogramName === POG_DYNAMIC_SLOT
  );
  const overstockPicks = availablePicks.filter(
    pick => pick.locations[pick.locationIndex]?.planogramName === POG_OVERSTOCK
  );
  const pogFogPicks = availablePicks.filter(
    pick =>
      pick.locations[pick.locationIndex]?.planogramName !== POG_DYNAMIC_SLOT &&
      pick.locations[pick.locationIndex]?.planogramName !== POG_OVERSTOCK &&
      pick.locations[pick.locationIndex]?.vdlSequence !== null
  );
  const otherPicks = availablePicks.filter(
    pick =>
      pick.locations[pick.locationIndex]?.planogramName !== POG_DYNAMIC_SLOT &&
      pick.locations[pick.locationIndex]?.planogramName !== POG_OVERSTOCK &&
      pick.locations[pick.locationIndex]?.vdlSequence === null
  );

  // Sort each group by proximity (itemSequence) with tie-breaker for current pick
  const sortByItemSequence = (a: Pick, b: Pick) => {
    const locationA = a.locations[a.locationIndex];
    const locationB = b.locations[b.locationIndex];
    const sequenceDiff = locationA.itemSequence - locationB.itemSequence;

    // If same itemSequence, prefer the current pick
    if (sequenceDiff === 0 && currentPickID) {
      const aIsCurrent = generatePickID(a) === currentPickID;
      const bIsCurrent = generatePickID(b) === currentPickID;
      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
    }

    return sequenceDiff;
  };

  const sortByVdlThenItemSequence = (a: Pick, b: Pick) => {
    const locationA = a.locations[a.locationIndex];
    const locationB = b.locations[b.locationIndex];
    if (locationA.vdlSequence !== null && locationB.vdlSequence !== null) {
      if (locationA.vdlSequence === locationB.vdlSequence) {
        const sequenceDiff = locationA.itemSequence - locationB.itemSequence;

        // If same itemSequence, prefer the current pick
        if (sequenceDiff === 0 && currentPickID) {
          const aIsCurrent = generatePickID(a) === currentPickID;
          const bIsCurrent = generatePickID(b) === currentPickID;
          if (aIsCurrent && !bIsCurrent) return -1;
          if (!aIsCurrent && bIsCurrent) return 1;
        }

        return sequenceDiff;
      }
      return locationA.vdlSequence - locationB.vdlSequence;
    }
    return 0;
  };

  // Sort each group
  dynamicSlotPicks.sort(sortByItemSequence);
  overstockPicks.sort(sortByItemSequence);
  pogFogPicks.sort(sortByVdlThenItemSequence);

  // Combine in priority order: DYNAMIC_SLOT > OVERSTOCK > POG_FOG > Others
  const sortedPicks = [...dynamicSlotPicks, ...overstockPicks, ...pogFogPicks, ...otherPicks];

  // Return first pick (closest)
  return sortedPicks[0] || null;
};

// Utility functions for sagas

export const getTokenData = (loginData: string): { accessToken: string; refreshToken: string } => {
  const {
    access_token: accessToken,
    refresh_token: refreshToken,
  }: { access_token: string; refresh_token: string } = JSON.parse(loginData);
  return { accessToken, refreshToken };
};

export const getUID = (): string | null => {
  const userData = sessionStorage.getItem(USER_DATA);
  if (userData) {
    const parsedUserData: UserData = JSON.parse(userData);
    const { sub } = parsedUserData;
    return sub || null;
  }
  return null;
};

export const createTokenDataCallback = (loginData: string) => {
  const { accessToken, refreshToken } = getTokenData(loginData);

  return accessToken && refreshToken
    ? async () => ({
        accessToken,
        refreshToken,
      })
    : null;
};

export const getLogType = (typeCode: string): LogType => {
  switch (typeCode) {
    case 'caution': {
      return 'warn';
    }
    case 'error': {
      return 'error';
    }
    case 'information': {
      return 'info';
    }
    case 'success': {
      return 'info';
    }
    default: {
      return 'info';
    }
  }
};

// System data utils

export const getAppVersion = async () => {
  const response = await axios.get('./version.json');
  const { version } = await response.data;

  return version || 0;
};

// Might not report correct version, sometimes browser will send back fake data to avoid sniffing.
export const getChromeVersion = () => {
  const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);

  return raw ? `${parseInt(raw[2], 10)}` : 'n/a';
};

export const getSystemDetails = () => {
  const deviceMemory = (navigator as any)?.deviceMemory || null;
  const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = (performance as any).memory || {};

  const convertMemorySizeToMB = (value: number) => `${Math.floor(value ? value / BYTE / BYTE : 0)}MB` || 'n/a';

  return {
    chromeVersion: getChromeVersion(),
    deviceMemory: deviceMemory ? `${deviceMemory}GB` : 'n/a',
    memoryLimit: convertMemorySizeToMB(jsHeapSizeLimit),
    memorySize: convertMemorySizeToMB(totalJSHeapSize),
    memoryUsed: convertMemorySizeToMB(usedJSHeapSize),
  };
};

export const getSortOrder = (type: string, ordersByType: OrdersByType) =>
  ordersByType[type as OrderType]?.sortOrder ?? 0;

// API Response Time Helper
export const createApiResponseTimeHelper = () => {
  const startTime = performance.now();
  return () => Math.round(performance.now() - startTime);
};

/**
 * Formats a destination string by padding numeric destinations to PADDING_LENGTH (5) digits
 * @param destination - The destination string to format
 * @returns Formatted destination string, or empty string if destination is null/undefined
 */

export const getFormattedDestination = (destination: string | null | undefined): string => {
  if (!destination || !destination.trim()) return '';
  // Pad numeric destinations to 5 digits
  return Number.isNaN(Number(destination)) ? destination : String(Number(destination)).padStart(PADDING_LENGTH, '0');
};
